import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import axios from '../../../utils/axios';
import { AxiosResponse } from 'axios';
import { API_URL } from '../../../constants';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import InventoryAdjustmentDetails from './InventoryAdjustmentDetails';
import { InventoryAdjustment } from '../../../services/inventory/types';

const InventoryAdjustmentDetailPage: React.FC = () => {
  const { inventoryAdjustmentId } = useParams<{ inventoryAdjustmentId: string }>();
  const history = useHistory();
  const [adjustment, setAdjustment] = useState<InventoryAdjustment | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAdjustment = async () => {
      if (!inventoryAdjustmentId) return;

      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${API_URL}/inventory/adjustments/${inventoryAdjustmentId}`,
        );
        setAdjustment((response as AxiosResponse<InventoryAdjustment>).data);
      } catch (error) {
        console.error('Error fetching inventory adjustment:', error);
        setError('Failed to load inventory adjustment details');
      } finally {
        setLoading(false);
      }
    };

    fetchAdjustment();
  }, [inventoryAdjustmentId]);

  const handleBack = () => {
    history.push('/inventory/adjustments');
  };

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
          Back to Adjustments
        </Button>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!adjustment) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
          Back to Adjustments
        </Button>
        <Typography variant="h6">Inventory adjustment not found</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} variant="outlined">
          Back to Adjustments
        </Button>
      </Box>
      <InventoryAdjustmentDetails adjustment={adjustment} />
    </Box>
  );
};

export default InventoryAdjustmentDetailPage;
